import React, { useEffect, useMemo } from 'react'
import { BaseComponentWithContextProps, componentWithContext } from '../../../../../base/customHooks/componentHOC'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import Translations from '../../../../../translations'
import { translate } from '../../../../../base/localization'
import IPButton from '../../../../../components/IPButton'
import { css } from '@emotion/css'
import IPModal from '../../../../../components/IPModal'
import { useCustomReducer } from '../../../../../base/customHooks'
import { IInsatallments, IParamPosInsatallment, IParamPosInsatallmentHistory, IPosData, PosProviderType } from '../models/models'
import Swal from 'sweetalert2'
import Table from '../../../../../components/Table'
import IPSelectBox from '../../../../../components/IPSelectBox'
import { ISelectOption } from '../../../../../components/IPSelectBox/IPSelectBox'
import IPInput from '../../../../../components/IPInput'
import { ListParamPosInsatallmentApi } from '../services/listParamPosInsatallmentApi'
import { DeleteByIdParamPosInsatallmentApi } from '../services/deleteByIdParamPosInsatallmentApi'
import { UpdateParamPosInsatallmentApi } from '../services/updateParamPosInsatallmentApi'
import { ListParamPosInsatallmentHistoryApi } from '../services/listParamPosInsatallmentHistoryApi'
import { DeleteParamPosInsatallmentApi } from '../services/deleteParamPosInsatallmentApi'
import { ListParatikaPosInsatallmentApi } from '../services/listParatikaPosInsatallmentApi'
import { DeleteByIdParatikaPosInsatallmentApi } from '../services/deleteByIdParatikaPosInsatallmentApi'
import { UpdateParatikaPosInsatallmentApi } from '../services/updateParatikaPosInsatallmentApi'
import { ListParatikaPosInsatallmentHistoryApi } from '../services/listParatikaPosInsatallmentHistoryApi'
import { DeleteParatikaPosInsatallmentApi } from '../services/deleteParatikaPosInsatallmentApi'
import { ListAkOdePosInsatallmentApi } from '../services/listAkOdePosInsatallmentApi'
import { DeleteByIdAkOdePosInsatallmentApi } from '../services/deleteByIdAkOdePosInsatallmentApi'
import { UpdateAkOdePosInsatallmentApi } from '../services/updateAkOdePosInsatallmentApi'
import { ListAkOdePosInsatallmentHistoryApi } from '../services/listAkOdePosInsatallmentHistoryApi'
import { DeleteAkOdePosInsatallmentApi } from '../services/deleteAkOdePosInsatallmentApi'
import { ListPosDataApi } from '../services/listPosDataApi'
import { getCompanyApi } from '../../../components/api'
import { ICompany } from '../../../components/models/models'

interface IPosListState {
  date: string
  posInsatallmentList: IParamPosInsatallment[]
  posInsatallmentListFilter: IParamPosInsatallment[]
  posInsatallment: IInsatallments[]
  showDetail: boolean
  showHistoryDetail: boolean
  posInsatallmentHistoryList: IParamPosInsatallmentHistory[]
  guid: string
  selectedCompany?: ISelectOption
  selectedPos?: ISelectOption
  companyOptions: ISelectOption[]
  posOptions: ISelectOption[]
  detailBankName: string
  historyBankName: string
  editingInstallmentId?: number
  editInstalmentRate?: ISelectOption
  editTermDay: string
  newInstalmentRate?: ISelectOption
  newTermDay: string
}

const posIdToType = (id: number): PosProviderType => (id === 2 ? 'paratika' : id === 3 ? 'akode' : 'param')
const posTypeToId = (type: PosProviderType): number => (type === 'paratika' ? 2 : type === 'akode' ? 3 : 1)

function PosList(props: BaseComponentWithContextProps) {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const t = translate(Translations)

  const [state, setState] = useCustomReducer<IPosListState>({
    date: new Date().toLocaleString('tr-TR'),
    posInsatallmentList: [],
    posInsatallment: [],
    posInsatallmentListFilter: [],
    showDetail: false,
    showHistoryDetail: false,
    posInsatallmentHistoryList: [],
    guid: '',
    companyOptions: [],
    posOptions: [],
    detailBankName: '',
    historyBankName: '',
    editTermDay: '',
    newTermDay: '',
  })

  const selectedPosId = Number(state.selectedPos?.value || 0)
  const currentPosType: PosProviderType = posIdToType(selectedPosId || posTypeToId((searchParams.get('posType') || 'param') as PosProviderType))

  const installmentAddNum = ['1', '2', '3', '4', '5', '6', '9', '12', '15', '18', '24'].map(value => ({
    value,
    label: t('pages.admin.BankAndInstallementOperation.bankCrud.bankAdd.installment', [value]),
  }))

  useEffect(() => {
    loadFilters()
  }, [])

  useEffect(() => {
    if (state.selectedPos) listPosInsatallment()
  }, [state.selectedPos])

  useEffect(() => {
    if (state.selectedCompany) {
      setState({ posInsatallmentListFilter: state.posInsatallmentList.filter(a => a.Company == state.selectedCompany?.value) })
    } else {
      setState({ posInsatallmentListFilter: state.posInsatallmentList })
    }
  }, [state.selectedCompany, state.posInsatallmentList])

  const loadFilters = async () => {
    props.showLoading()
    try {
      const [posData, companies] = await Promise.all([new ListPosDataApi({}).listPosData(), getCompanyApi() as Promise<ICompany[]>])

      const posOptions = (posData || []).map((x: IPosData) => ({ label: x.Name, value: x.Id }))
      const companyOptions = (companies || []).map((x: ICompany) => ({ label: x.Name, value: x.Code }))

      const routePosType: PosProviderType = (searchParams.get('posType') || 'param') as PosProviderType
      const initialPos = posOptions.find(x => Number(x.value) === posTypeToId(routePosType)) || posOptions[0]

      const routeCompany = searchParams.get('company')
      const initialCompany = routeCompany ? companyOptions.find(x => String(x.value) === routeCompany) : undefined

      setState({ posOptions, companyOptions, selectedPos: initialPos, selectedCompany: initialCompany })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err.description, showConfirmButton: true, allowOutsideClick: false })
    } finally {
      props.hideLoading()
    }
  }

  const fetchPosInsatallmentList = async () =>
    currentPosType === 'paratika'
      ? await new ListParatikaPosInsatallmentApi({}).listParatikaPosInsatallment()
      : currentPosType === 'akode'
        ? await new ListAkOdePosInsatallmentApi({}).listAkOdePosInsatallment()
        : await new ListParamPosInsatallmentApi({}).listParamPosInsatallment()

  const listPosInsatallment = async () => {
    props.showLoading()
    try {
      const res = await fetchPosInsatallmentList()
      setState({ posInsatallmentList: res, posInsatallmentListFilter: res })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err.description, showConfirmButton: true, allowOutsideClick: false })
    } finally {
      props.hideLoading()
    }
  }

  const persistInstallmentChange = async (insatallments: IInsatallments[]) => {
    const record = state.posInsatallmentList.find(l => l.Guid === state.guid)
    if (!record) return
    const req: IParamPosInsatallment = { ...record, Insatallments: insatallments }
    props.showLoading()
    try {
      if (currentPosType === 'paratika') await new UpdateParatikaPosInsatallmentApi({}).updateParatikaPosInsatallment(req)
      else if (currentPosType === 'akode') await new UpdateAkOdePosInsatallmentApi({}).updateAkOdePosInsatallment(req)
      else await new UpdateParamPosInsatallmentApi({}).updateParamPosInsatallment(req)

      const res = await fetchPosInsatallmentList()
      const updated = res.find(r => r.Guid === state.guid)
      setState({
        posInsatallmentList: res,
        posInsatallment: updated?.Insatallments || [],
        editingInstallmentId: undefined,
        editInstalmentRate: undefined,
        editTermDay: '',
        newInstalmentRate: undefined,
        newTermDay: '',
      })
      Swal.fire({ icon: 'success', title: 'İşlem başarılı', showConfirmButton: true, allowOutsideClick: false })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err.description, showConfirmButton: true, allowOutsideClick: false })
    } finally {
      props.hideLoading()
    }
  }

  const startEditInstallment = (ins: IInsatallments) => {
    setState({
      editingInstallmentId: ins.Id,
      editInstalmentRate: installmentAddNum.find(o => String(o.value) === String(ins.Installment)) || { value: String(ins.Installment), label: String(ins.Installment) },
      editTermDay: String(ins.InterestRate),
    })
  }

  const cancelEditInstallment = () => setState({ editingInstallmentId: undefined, editInstalmentRate: undefined, editTermDay: '' })

  const saveEditInstallment = () => {
    if (!state.editInstalmentRate || !state.editTermDay) {
      Swal.fire({ icon: 'error', title: 'Lütfen boş alan bırakmayınız', showConfirmButton: true, allowOutsideClick: false })
      return
    }
    if (state.posInsatallment.some(ins => ins.Id !== state.editingInstallmentId && String(ins.Installment) === String(state.editInstalmentRate?.value))) {
      Swal.fire({ icon: 'error', title: 'Taksit daha önce seçilmiştir.', showConfirmButton: true, allowOutsideClick: false })
      return
    }
    const updatedInsatallments = state.posInsatallment.map(ins =>
      ins.Id === state.editingInstallmentId ? { ...ins, Installment: Number(state.editInstalmentRate?.value), InterestRate: Number(state.editTermDay) } : ins
    )
    persistInstallmentChange(updatedInsatallments)
  }

  const addDetailInstallment = () => {
    if (!state.newInstalmentRate || !state.newTermDay) {
      Swal.fire({ icon: 'error', title: 'Lütfen boş alan bırakmayınız', showConfirmButton: true, allowOutsideClick: false })
      return
    }
    if (state.posInsatallment.some(ins => String(ins.Installment) === String(state.newInstalmentRate?.value))) {
      Swal.fire({ icon: 'error', title: 'Taksit daha önce seçilmiştir.', showConfirmButton: true, allowOutsideClick: false })
      return
    }
    const bankRef = state.posInsatallment[0]
    const newItem: IInsatallments = {
      Id: 0,
      BankId: bankRef?.BankId,
      BankName: bankRef?.BankName,
      Installment: Number(state.newInstalmentRate.value),
      InterestRate: Number(state.newTermDay),
    }
    persistInstallmentChange([...state.posInsatallment, newItem])
  }

  const listPosInsatallmentHistory = async (guid: string, bankName: string) => {
    props.showLoading()
    try {
      const res =
        currentPosType === 'paratika'
          ? await new ListParatikaPosInsatallmentHistoryApi({}).listParatikaPosInsatallmentHistory(guid)
          : currentPosType === 'akode'
            ? await new ListAkOdePosInsatallmentHistoryApi({}).listAkOdePosInsatallmentHistory(guid)
            : await new ListParamPosInsatallmentHistoryApi({}).listParamPosInsatallmentHistory(guid)
      setState({ showHistoryDetail: true, posInsatallmentHistoryList: res, historyBankName: bankName })
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: err.description, showConfirmButton: true, allowOutsideClick: false })
    } finally {
      props.hideLoading()
    }
  }

  const deletePosInsatallment = async (guid: string) => {
    Swal.fire({ icon: 'question', title: 'Silmek istediğinize emin misiniz?', showConfirmButton: true, allowOutsideClick: false, showDenyButton: true, confirmButtonText: 'Evet', denyButtonText: 'Hayır' }).then(async result => {
      if (!result.isConfirmed) return
      props.showLoading()
      try {
        if (currentPosType === 'paratika') await new DeleteParatikaPosInsatallmentApi({}).deleteParatikaPosInsatallment(guid)
        else if (currentPosType === 'akode') await new DeleteAkOdePosInsatallmentApi({}).deleteAkOdePosInsatallment(guid)
        else await new DeleteParamPosInsatallmentApi({}).deleteParamPosInsatallment(guid)
        setState({ posInsatallmentList: state.posInsatallmentList.filter(fil => fil.Guid !== guid) })
        Swal.fire({ icon: 'success', title: 'İşlem başarılı', showConfirmButton: true, allowOutsideClick: false })
      } catch (err: any) {
        Swal.fire({ icon: 'error', title: err.description, showConfirmButton: true, allowOutsideClick: false })
      } finally {
        props.hideLoading()
      }
    })
  }

  const deleteByIdPosInsatallment = async (id: number) => {
    Swal.fire({ icon: 'question', title: 'Silmek istediğinize emin misiniz?', showConfirmButton: true, allowOutsideClick: false, showDenyButton: true, confirmButtonText: 'Evet', denyButtonText: 'Hayır' }).then(async result => {
      if (!result.isConfirmed) return
      props.showLoading()
      try {
        if (currentPosType === 'paratika') await new DeleteByIdParatikaPosInsatallmentApi({}).deleteByIdParatikaPosInsatallment(id)
        else if (currentPosType === 'akode') await new DeleteByIdAkOdePosInsatallmentApi({}).deleteByIdAkOdePosInsatallment(id)
        else await new DeleteByIdParamPosInsatallmentApi({}).deleteByIdParamPosInsatallment(id)

        const posInsatallment = state.posInsatallment.filter(ins => ins.Id !== id)
        let posInsatallmentList: IParamPosInsatallment[] = []
        if (posInsatallment.length > 0) {
          state.posInsatallmentList.forEach(list => {
            if (list.Guid === state.guid) list.Insatallments = posInsatallment
            posInsatallmentList.push(list)
          })
        } else {
          posInsatallmentList = state.posInsatallmentList.filter(fil => fil.Guid !== state.guid)
          setState({ showDetail: false })
        }
        setState({ posInsatallmentList, posInsatallment })
      } catch (err: any) {
        Swal.fire({ icon: 'error', title: err.description, showConfirmButton: true, allowOutsideClick: false })
      } finally {
        props.hideLoading()
      }
    })
  }

  const columns = useMemo(() => {
    const cols = [
      { Header: '#', accessor: 'col1' },
      { Header: 'Banka', accessor: 'col2' },
      { Header: 'Şirket', accessor: 'comp' },
    ]

    // Eğer listedeki herhangi bir kayıt Datagate ise Genel Tanım sütunu ekle
    if (state.posInsatallmentListFilter.some(ins => ins.Company === 1100)) {
      cols.push({ Header: 'Bayi Tanım', accessor: 'generalDef' })
    }

    cols.push(
      { Header: 'Detay', accessor: 'col3' },
      { Header: 'Başlangıç Tarihi', accessor: 'col4' },
      { Header: 'Bitiş Tarihi', accessor: 'col5' },
      { Header: 'Değişiklikler', accessor: 'col6' },
      { Header: 'İşlemler', accessor: 'col7' }
    )

    return cols
  }, [state.posInsatallmentListFilter])

  const tableData = useMemo(() =>
    state.posInsatallmentListFilter.map((ins, index) => ({
      col1: index + 1,
      col2: ins.Insatallments[0]?.BankName,
      col3: <IPButton text='Detay' onClick={() => setState({ showDetail: true, posInsatallment: ins.Insatallments, guid: ins.Guid, detailBankName: ins.Insatallments[0]?.BankName || '' })} type='button' style={{ padding: '0px', width: '120px', height: '30px', color: 'white', borderColor: 'red' }} className='btn btn-danger' />,
      col4: new Date(ins.StartDate).toLocaleString('tr-TR'),
      col5: new Date(ins.EndDate).toLocaleString('tr-TR'),
      col6: <IPButton text='Tarihçe' onClick={() => listPosInsatallmentHistory(ins.Guid, ins.Insatallments[0]?.BankName || '')} type='button' style={{ padding: '0px', width: '120px', height: '30px', color: 'white', borderColor: 'red' }} className='btn btn-danger' />,
      col7: <>
        <a data-action-type='edit' role='button' className={css`cursor: pointer;`} data-toggle='tooltip' data-original-title={t('pages.mySpecial.userUpdate.edit')} onClick={() => navigate(`/admin/BankAndInstallementOperation/Pos/Pos?posType=${currentPosType}`, { state: { installementData: ins, flag: 'update' } })}><i className={`${css`font-size: 1.2rem; color: green;`} icon-edit`}></i></a>
        <a onClick={() => deletePosInsatallment(ins.Guid)} data-action-type='delete' role='button' className={css`cursor: pointer;`} data-toggle='tooltip' data-original-title={t('pages.mySpecial.userUpdate.delete')}><i className={`${css`font-size: 1.2rem; color: red;`} icon-trash-2`}></i></a>
      </>,
      comp: state.companyOptions.find(a => Number(a.value) == ins.Company)?.label,
      generalDef: ins.Company === 1100 ? (ins.IsVodafone ? 'Vodafone' : 'MIX') : '-',
    })), [state.posInsatallmentListFilter, state.companyOptions, currentPosType])

  return (
    <div className=''>
      <div className='content col-lg-12 m-auto'>
        <div className='card'>
          <div className='card-header d-flex justify-content-between align-items-center'>
            <div className='col-12 col-md-9 mb-2'><span className='h4'>{state.selectedPos?.label} Taksit Listeleme</span></div>
            <div className='col-12 col-md-2 mb-2'><div className='buttonsuz'><Link to={`/admin/BankAndInstallementOperation/Pos/Pos?posType=${currentPosType}${state.selectedCompany ? `&company=${state.selectedCompany.value}` : ''}`}><IPButton text={t('pages.admin.BankAndInstallementOperation.bankCrud.bankUpdateDel.add')} className='btn btn-primary' style={{ height: '45px', marginLeft: '15px' }} /></Link></div></div>
          </div>
          <div className='card-body'>
            <div className='row'>
              <div className='form-group col-md-2'><IPSelectBox isClearable={false} options={state.posOptions} value={state.selectedPos} onChangeSingle={option => {
                setState({ selectedPos: option })
                setSearchParams(prev => {
                  const p = new URLSearchParams(prev)
                  if (option) p.set('posType', posIdToType(Number(option.value)))
                  else p.delete('posType')
                  return p
                }, { replace: true })
              }} /></div>
              <div className='form-group col-md-2'><IPSelectBox isClearable placeholder='Şirket' options={state.companyOptions} value={state.selectedCompany} onChangeSingle={option => {
                setState({ selectedCompany: option })
                setSearchParams(prev => {
                  const p = new URLSearchParams(prev)
                  if (option) p.set('company', String(option.value))
                  else p.delete('company')
                  return p
                }, { replace: true })
              }} /></div>
              <div className='col-lg-12'><div id='datatable_wrapper' className='dataTables_wrapper dt-bootstrap4' style={{ padding: '10px' }}><div className='row'><div className='col-sm-12 pr-4' style={{ overflowX: 'auto' }}><Table columns={columns} data={tableData} currentPage={0} /></div></div></div></div>
            </div>
          </div>
        </div>
      </div>

      <IPModal show={state.showHistoryDetail} onClose={() => setState({ showHistoryDetail: false, historyBankName: '' })} hideConfirm={true} width={1200} title={[state.historyBankName, state.selectedPos?.label, 'Taksit Tarihçe'].filter(Boolean).join(' ')}>
        <table id='datatable' className='table table-bordered table-hover dataTable' role='grid' aria-describedby='datatable_info'>
          <thead className='tableodd'><tr role='row'><th>#</th><th>Değişiklik Tarihi</th><th>Başlangıç Tarihi</th><th>Bitiş Tarihi</th><th>Taksit</th><th>Vade</th><th>Kullanıcı</th><th>İşlem Detayı</th></tr></thead>
          <tbody>{state.posInsatallmentHistoryList.map((history, index) => <tr role='row' className='odd' key={`${history.Guid}-${history.CreatedDate}-${index}`}><td>{index + 1}</td><td>{new Date(history.CreatedDate).toLocaleString('tr-TR')}</td><td>{new Date(history.StartDate).toLocaleString('tr-TR')}</td><td>{new Date(history.EndDate).toLocaleString('tr-TR')}</td><td>{history.Installment}</td><td>{history.InterestRate}</td><td>{history.UserName}</td><td>{history.ProcessInformation}</td></tr>)}</tbody>
        </table>
      </IPModal>

      <IPModal show={state.showDetail} onClose={() => setState({ showDetail: false, detailBankName: '', editingInstallmentId: undefined, editInstalmentRate: undefined, editTermDay: '', newInstalmentRate: undefined, newTermDay: '' })} hideConfirm={true} width={1000} title={[state.detailBankName, state.selectedPos?.label, 'Taksit Detay'].filter(Boolean).join(' ')}>
        <table id='datatable' className='table table-bordered table-hover dataTable' role='grid' aria-describedby='datatable_info'>
          <thead className='tableodd'><tr role='row'><th>Taksit</th><th>Vade(max)</th><th>İşlem</th></tr></thead>
          <tbody>
            {state.posInsatallment.map(installment => (
              <tr role='row' className='odd' key={installment.Id}>
                {state.editingInstallmentId === installment.Id ? (
                  <>
                    <td style={{ minWidth: '160px' }}><IPSelectBox isClearable menuPortalTarget={document.body} options={installmentAddNum} value={state.editInstalmentRate} onChangeSingle={option => setState({ editInstalmentRate: option })} /></td>
                    <td style={{ minWidth: '120px' }}><IPInput name='editTermDay' type='text' value={state.editTermDay} onChange={e => setState({ editTermDay: e.target.value?.replace(',', '.') })} /></td>
                    <td>
                      <a onClick={saveEditInstallment} role='button' className={css`cursor: pointer;`} data-toggle='tooltip' data-original-title='Kaydet'><i className={`${css`font-size: 1.2rem; color: green;`} icon-check`}></i></a>
                      <a onClick={cancelEditInstallment} role='button' className={css`cursor: pointer;`} data-toggle='tooltip' data-original-title='İptal'><i className={`${css`font-size: 1.2rem; color: gray;`} icon-x`}></i></a>
                    </td>
                  </>
                ) : (
                  <>
                    <td>{installment.Installment}</td>
                    <td>{installment.InterestRate}</td>
                    <td>
                      <a onClick={() => startEditInstallment(installment)} role='button' className={css`cursor: pointer;`} data-toggle='tooltip' data-original-title='Düzenle'><i className={`${css`font-size: 1.2rem; color: green;`} icon-edit`}></i></a>
                      <a onClick={() => deleteByIdPosInsatallment(installment.Id)}><i className='icon-trash-2' style={{ fontSize: '1.2rem', color: 'red', cursor: 'pointer' }}></i></a>
                    </td>
                  </>
                )}
              </tr>
            ))}
            <tr role='row' className='even'>
              <td style={{ minWidth: '160px' }}><IPSelectBox isClearable menuPortalTarget={document.body} placeholder='Taksit' options={installmentAddNum} value={state.newInstalmentRate} onChangeSingle={option => setState({ newInstalmentRate: option })} /></td>
              <td style={{ minWidth: '120px' }}><IPInput name='newTermDay' type='text' placeholder='Vade' value={state.newTermDay} onChange={e => setState({ newTermDay: e.target.value?.replace(',', '.') })} /></td>
              <td><IPButton text='Ekle' type='button' style={{ padding: '0px', width: '80px', height: '30px' }} className='btn btn-success' onClick={addDetailInstallment} /></td>
            </tr>
          </tbody>
        </table>
      </IPModal>
    </div>
  )
}

export default componentWithContext(PosList)
